import { convertToConversationsChatMessage } from "@mirage/service-conversation/utils/convertConversationMessages";
import i18n from "@mirage/translations";
import type {
  ConversationAssistantMessage,
  ConversationMessage,
  ConversationUserMessage,
  NewToolResponsesMessage,
  NewUserMessage,
} from "../types";

// logic for processing posting new messages + getting responses

interface PendingMessages {
  pendingToolMessages: ConversationMessage[];
  pendingMessage:
    | ConversationMessage<
        ConversationUserMessage | ConversationAssistantMessage
      >
    | undefined;
}

/**
 * Convert params for a new message post into ConversationMessage objects
 */
export function createPendingMessages(
  newMessage: NewUserMessage | NewToolResponsesMessage,
): PendingMessages {
  const pendingToolMessages: ConversationMessage[] = [];
  if (newMessage.toolResponses) {
    for (const toolResponse of newMessage.toolResponses) {
      pendingToolMessages.push({
        id: `pending-${Date.now()}-${toolResponse.value.toolCallId}`,
        timestamp: new Date(),
        messageType: toolResponse,
        status: "pending",
      });
    }
  }

  switch (newMessage.messageType) {
    case "userMessage":
    case undefined: {
      const pendingMessage: ConversationMessage<ConversationUserMessage> = {
        id: `pending-${Date.now()}`,
        timestamp: new Date(),
        messageType: {
          case: "userMessage",
          value: {
            text: newMessage.message,
            attachments: newMessage.attachments || [],
            rephrased: newMessage.rephrased || "",
            llmIgnore: newMessage.llmIgnore || false,
          },
        },
        status: "pending",
      };
      return {
        pendingToolMessages,
        pendingMessage,
      };
    }
    case "assistantMessage": {
      const pendingMessage: ConversationMessage<ConversationAssistantMessage> =
        {
          id: `pending-${Date.now()}`,
          timestamp: new Date(),
          messageType: {
            case: "assistantMessage",
            value: {
              text: newMessage.message,
              cited: [],
              context: [],
              isCached: false,
            },
          },
          status: "pending",
        };
      return {
        pendingToolMessages,
        pendingMessage,
      };
    }
    case "toolResponses": {
      // pure tool responses do not have a new user message
      return {
        pendingToolMessages,
        pendingMessage: undefined,
      };
    }
    default:
      newMessage satisfies never;
      throw new Error(
        `Unknown message type: ${(newMessage as NewUserMessage).messageType}`,
      );
  }
}

/**
 * Get args for the getResponse API call based on the pending messages
 */
export function getPendingMessagesResponseArgs(
  priorHistory: ConversationMessage[],
  pendingMessages: ReturnType<typeof createPendingMessages>,
) {
  // history includes pending tool responses but excludes the new message (that's sent through
  // the request's params directly)
  const historyMessages = [
    ...priorHistory,
    ...pendingMessages.pendingToolMessages,
  ].map((message) => convertToConversationsChatMessage(message));

  if (pendingMessages.pendingMessage) {
    const messageText = pendingMessages.pendingMessage.messageType.value.text;
    if (pendingMessages.pendingMessage.messageType.case === "userMessage") {
      return {
        historyMessages,
        messageText,
        rephrased: pendingMessages.pendingMessage.messageType.value.rephrased,
        llmIgnoreUserMessage:
          pendingMessages.pendingMessage.messageType.value.llmIgnore || false,
      };
    }
    return {
      historyMessages,
      messageText,
      rephrased: "",
      llmIgnoreUserMessage: false,
    };
  } else {
    // tool-only response should ignore mark the new user message as llmIgnore
    return {
      historyMessages,
      messageText: "",
      rephrased: "",
      llmIgnoreUserMessage: true,
    };
  }
}

/**
 * Get messages to return to UI when the getResponse API call is in progress
 */
export function getInProgressConversationsResponseMessages(
  priorHistory: ConversationMessage[],
  pendingMessages: PendingMessages,
) {
  const historyWithPendingMessages = [
    ...priorHistory,
    ...pendingMessages.pendingToolMessages,
  ];
  if (pendingMessages.pendingMessage) {
    historyWithPendingMessages.push(pendingMessages.pendingMessage);
  }
  return historyWithPendingMessages;
}

/**
 * Get messages to return to UI when the getResponse API call fails
 */
export function getErrorConversationsResponseMessages(
  priorHistory: ConversationMessage[],
  pendingMessages: PendingMessages,
) {
  const errorResponseMessage: ConversationMessage = {
    id: `error-${Date.now()}`,
    timestamp: new Date(),
    messageType: {
      case: "assistantMessage",
      value: {
        text: i18n.t("compose_generic_error"),
        cited: [],
        context: [],
        isCached: false,
      },
    },
    status: "error",
  };
  const allPendingMessages: ConversationMessage[] = [
    ...pendingMessages.pendingToolMessages,
  ];
  if (pendingMessages.pendingMessage) {
    allPendingMessages.push(pendingMessages.pendingMessage);
  }
  const conversationMessages: ConversationMessage[] = [
    ...priorHistory,
    ...allPendingMessages.map(
      (message): ConversationMessage => ({
        ...message,
        status: "success",
      }),
    ), // mark all pending messages as success
    errorResponseMessage,
  ];
  return conversationMessages;
}
